import { ENDPOINT } from '~/constants';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type UnitMappingsResponse,
  type UnitMappingsSearchParams,
} from './types';
import { zodUnitMappingsSearchParamsSchema } from './validations';

/**
 * Fetches all unit mappings from the API.
 * @param searchParams - The search parameters for filtering unit mappings.
 * @returns The paginated unit mappings response.
 * @throws Error if the API request fails.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Units/operation/get_all_unit_mappings_units_unit_mapping_get
 */
export const fetchUnitMappings = async (
  searchParams: UnitMappingsSearchParams = {},
) => {
  try {
    const response = await vestigasApi
      .get(ENDPOINT.UNITS.GET_ALL_MAPPINGS(), {
        searchParams,
        validate: {
          searchParams: zodUnitMappingsSearchParamsSchema,
        },
      })
      .json<UnitMappingsResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error fetching unit mappings',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(($) => $.unit.fetchUnitMappings.error422, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(($) => $.unit.fetchUnitMappings.error.generic, {
            ns: 'data',
          });
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
